import { FC } from "react";
import { Box, ButtonBase, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { UserMenu } from "../navigation/UserMenu";
import { tokens } from "../../theme";
import type { MainSection } from "./headerDesktop";

export type { MainSection };

const SECTION_LABEL: Record<MainSection, string> = {
  lpp: "LPP Monitor",
  reports: "Reports",
  trading: "Dipster / Dumper",
};

interface HeaderMobileProps {
  currentSection: MainSection;
  /** Label of the selected bot family, null on the overview. */
  selectionLabel: string | null;
  onOpenBots?: () => void;
  onLogoClick: () => void;
  /** Shown instead of the logo inside a family: 44px back chevron to the overview. */
  onBack?: () => void;
  /** Bots needing attention in the visible scope (the family, or everything on the overview). */
  alertCount?: number;
  /** "error" when any of them is critical, "warning" otherwise. */
  alertTone?: "error" | "warning";
  lastUpdate?: number;
  email?: string;
  groups: string[];
  signOut: () => void;
  onSelectBots?: () => void;
}

/** Small count badge tinted with the alert tone (red = critical, amber = needs action). */
const AlertCountPill: FC<{ count: number; tone: "error" | "warning" }> = ({ count, tone }) => {
  const theme = useTheme();
  const color = theme.palette[tone].main;
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        height: 20,
        minWidth: 20,
        px: 0.75,
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        color,
        bgcolor: alpha(color, 0.16),
        border: `1px solid ${alpha(color, 0.34)}`,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
};

/** Compact glass top bar for phones: brand, bot picker and account menu. */
const HeaderMobile: FC<HeaderMobileProps> = ({
  currentSection,
  selectionLabel,
  onOpenBots,
  onLogoClick,
  onBack,
  alertCount = 0,
  alertTone = "warning",
  lastUpdate,
  email,
  groups,
  signOut,
  onSelectBots,
}) => (
  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", height: "100%" }}>
    {onBack ? (
      <IconButton
        onClick={onBack}
        aria-label="Back to overview"
        sx={{
          width: 44,
          height: 44,
          ml: -1,
          color: "text.primary",
          bgcolor: "rgba(255,255,255,0.06)",
          border: `1px solid ${tokens.glass.border}`,
          borderRadius: "50%",
        }}
      >
        <ChevronLeftRoundedIcon />
      </IconButton>
    ) : (
      <Box
        component="button"
        onClick={onLogoClick}
        aria-label="Go to overview"
        sx={{ border: 0, background: "none", p: 0, height: 44, display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <img src="/logo.png" alt="Teks" height={20} />
      </Box>
    )}

    {currentSection !== "lpp" || !onOpenBots ? (
      // Reports / Trading show a large page title; keep the bar quiet.
      <Box sx={{ flex: 1 }} aria-label={SECTION_LABEL[currentSection]} />
    ) : (
      <ButtonBase
        onClick={onOpenBots}
        aria-label={
          alertCount > 0
            ? `Choose bot (${alertCount} ${alertCount === 1 ? "bot needs" : "bots need"} attention)`
            : "Choose bot"
        }
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "flex-start",
          gap: 1,
          height: 44,
          pl: 2,
          pr: 1.25,
          borderRadius: 999,
          background: tokens.glass.bg,
          border: `1px solid ${tokens.glass.border}`,
          boxShadow: tokens.glass.highlight,
          "&:active": { bgcolor: "rgba(255,255,255,0.08)" },
        }}
      >
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 650, minWidth: 0 }} noWrap>
          {selectionLabel ?? "Overview"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {alertCount > 0 && <AlertCountPill count={alertCount} tone={alertTone} />}
        <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
      </ButtonBase>
    )}

    <UserMenu
      email={email}
      groups={groups}
      lastUpdate={lastUpdate}
      onSignOut={signOut}
      onSelectBots={onSelectBots}
    />
  </Stack>
);

export default HeaderMobile;
